/**
 * By.com shop experience
 * - Desktop: card swaps to the 2nd image; fullscreen background reveals the 3rd
 * - Card to card: next photo reveals over the current one (no white gap)
 * - Desktop hover: slight cursor-follow parallax on the fullscreen third image
 * - Desktop click: stay on the page — lock the row, pushState the product URL
 * - Mobile: list only; a row navigates to the product page. Photos swipe.
 * - Product template: overlay z-index stacking
 * - Desktop grid: Lenis inertia scroll (GSAP-style lerp); proximity snap when row bands exist
 */
(() => {
  const mqDesktop = window.matchMedia("(min-width: 769px)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const DETAIL_SCROLL_MARGIN = 48;
  /** Fullscreen third-image hover is off until we turn this back on. */
  const hoverBackgroundEnabled = false;

  const root = document.querySelector("[data-shop-experience]");
  if (!root) return;

  const mode = root.dataset.mode || "grid";
  const hoverLayer = root.querySelector("[data-shop-hover]");
  const detail = root.querySelector("[data-shop-detail]");
  const panel = root.querySelector("[data-shop-panel]");
  const panelIntro = root.querySelector("[data-shop-panel-intro]");
  const panelAbout = root.querySelector("[data-shop-panel-about]");
  let lastProductHandle = null;
  const overlayA = root.querySelector('[data-overlay="a"]');
  const overlayB = root.querySelector('[data-overlay="b"]');
  const cards = () => [...root.querySelectorAll("[data-shop-card]")];
  const bands = () => [...root.querySelectorAll(".shop-grid__band")];

  let activeCard = null;
  let detailOpen = false;
  let historyOwned = false;
  const hoverNodes = new WeakMap();

  /** Lenis + proximity snap (desktop grid only) */
  let lenis = null;
  let bandSnap = null;
  let lenisRafId = null;

  function clearNode(node) {
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function clonePreload(card, selector, className) {
    const source = card.querySelector(selector);
    if (!source) return null;

    if (source.tagName === "IMG") {
      const img = document.createElement("img");
      img.src = source.currentSrc || source.src;
      img.alt = "";
      img.className = className;
      img.decoding = "async";
      return img;
    }

    return null;
  }

  /** Warm decode so first hover is a fade, not a fetch */
  function warmCardMedia() {
    cards().forEach((card) => {
      card.querySelectorAll("[data-card-preload] img").forEach((img) => {
        if (typeof img.decode === "function") {
          img.decode().catch(() => {});
        }
      });

      const hoverSource = card.querySelector("[data-preload-hover]");
      if (hoverSource && hoverSource.tagName === "IMG") {
        const warm = new Image();
        warm.src = hoverSource.currentSrc || hoverSource.src;
        hoverNodes.set(card, warm);
      }
    });
  }

  /** Start larger than the card photo; expand on open, instant hide on close */
  const HOVER_START_PAD = 0.18;
  /** Brief grace so leave → re-enter same card doesn't blink */
  const HOVER_CLOSE_GRACE_MS = 100;
  const FULL_INSET = { top: 0, right: 0, bottom: 0, left: 0 };
  /** Slight oversize + max offset so the hover photo can track the cursor */
  const PARALLAX_SCALE = 1.08;
  const PARALLAX_STRENGTH = 28;

  let hoverCloseTimer = null;
  let hoverImageEl = null;
  let hoverGeneration = 0;
  let parallaxRaf = null;
  let parallaxX = 0;
  let parallaxY = 0;
  /** Fallback if transitionend doesn't fire; longer than --shop-hover-reveal (480ms) */
  const HOVER_REVEAL_MS = 700;

  function cancelHoverClose() {
    if (hoverCloseTimer) {
      window.clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
    }
  }

  function requestHover(card) {
    cancelHoverClose();
    // Same card after a brief leave — keep current state, don't re-animate
    if (card.classList.contains("is-hovered")) return;
    setHoverBackground(card);
  }

  function requestHoverClear() {
    cancelHoverClose();
    hoverCloseTimer = window.setTimeout(() => {
      hoverCloseTimer = null;
      clearHover();
    }, HOVER_CLOSE_GRACE_MS);
  }

  function cardFrameRect(card) {
    const frame =
      card.querySelector(".shop-card__frame") ||
      card.querySelector("[data-shop-card-media]") ||
      card;
    return frame.getBoundingClientRect();
  }

  function insetFromRect(rect, padRatio = 0) {
    const padX = rect.width * padRatio;
    const padY = rect.height * padRatio;
    return {
      top: Math.max(0, rect.top - padY),
      left: Math.max(0, rect.left - padX),
      right: Math.max(0, window.innerWidth - rect.right - padX),
      bottom: Math.max(0, window.innerHeight - rect.bottom - padY),
    };
  }

  function setWindowInset(windowEl, imageEl, inset) {
    const { top, right, bottom, left } = inset;
    windowEl.style.top = `${top}px`;
    windowEl.style.right = `${right}px`;
    windowEl.style.bottom = `${bottom}px`;
    windowEl.style.left = `${left}px`;
    imageEl.style.width = `${window.innerWidth}px`;
    imageEl.style.height = `${window.innerHeight}px`;
    imageEl.style.top = `${-top}px`;
    imageEl.style.left = `${-left}px`;
  }

  function withTransitions(windowEl, imageEl, enabled) {
    const value = enabled ? "" : "none";
    windowEl.style.transition = value;
    imageEl.style.transition = value;
  }

  function applyParallaxTransform() {
    parallaxRaf = null;
    if (!hoverImageEl) return;
    hoverImageEl.style.transform = `translate(${parallaxX}px, ${parallaxY}px) scale(${PARALLAX_SCALE})`;
  }

  function setParallaxFromPointer(clientX, clientY) {
    if (!hoverImageEl || reduceMotion.matches) return;
    const nx = (clientX / window.innerWidth) * 2 - 1;
    const ny = (clientY / window.innerHeight) * 2 - 1;
    parallaxX = nx * PARALLAX_STRENGTH;
    parallaxY = ny * PARALLAX_STRENGTH;
    if (!parallaxRaf) {
      parallaxRaf = requestAnimationFrame(applyParallaxTransform);
    }
  }

  function resetParallax() {
    if (parallaxRaf) {
      cancelAnimationFrame(parallaxRaf);
      parallaxRaf = null;
    }
    hoverImageEl = null;
    parallaxX = 0;
    parallaxY = 0;
  }

  /** Drop every photo except the one that just finished covering the viewport */
  function settleHoverWindow(windowEl, generation) {
    if (generation !== hoverGeneration) return;
    if (!hoverLayer || !windowEl.isConnected || !hoverLayer.classList.contains("is-active")) return;
    [...hoverLayer.children].forEach((child) => {
      if (child !== windowEl) child.remove();
    });
  }

  /** Snap in-flight photos to fullscreen so the next reveal has a full image underneath */
  function parkOpenHoverWindows() {
    if (!hoverLayer) return;
    [...hoverLayer.children].forEach((child) => {
      const imageEl = child.querySelector(".shop-hover__image, .shop-hover__placeholder");
      if (!imageEl) return;
      withTransitions(child, imageEl, false);
      setWindowInset(child, imageEl, FULL_INSET);
    });
  }

  function setHoverBackground(card) {
    if (!hoverLayer || !mqDesktop.matches) return;

    const node = hoverBackgroundEnabled
      ? clonePreload(card, "[data-preload-hover]", "shop-hover__image") ||
        (() => {
          const url = card.dataset.hoverUrl;
          if (!url) return null;
          const img = document.createElement("img");
          img.src = url;
          img.alt = "";
          img.className = "shop-hover__image";
          return img;
        })()
      : null;

    resetParallax();
    cards().forEach((other) => {
      if (other !== card) other.classList.remove("is-hovered");
    });

    document.body.classList.add("is-shop-hover");
    card.classList.add("is-hovered");

    if (!node) {
      hoverGeneration += 1;
      hoverLayer.classList.remove("is-active");
      clearNode(hoverLayer);
      return;
    }

    const keepCurrentPhoto =
      hoverLayer.classList.contains("is-active") && hoverLayer.childElementCount > 0;

    const windowEl = document.createElement("div");
    windowEl.className = "shop-hover__window";
    const startInset = insetFromRect(cardFrameRect(card), HOVER_START_PAD);

    // Park at start size with transitions off so the first paint isn't mid-tween.
    // A card-to-card move keeps the previous photo fullscreen underneath.
    if (keepCurrentPhoto) parkOpenHoverWindows();
    else clearNode(hoverLayer);

    withTransitions(windowEl, node, false);
    setWindowInset(windowEl, node, startInset);
    node.style.transform = `translate(0px, 0px) scale(${PARALLAX_SCALE})`;
    windowEl.appendChild(node);
    hoverLayer.appendChild(windowEl);
    hoverImageEl = node;

    hoverLayer.classList.add("is-active");

    const generation = ++hoverGeneration;
    let settled = false;
    const coversViewport = () => {
      const rect = windowEl.getBoundingClientRect();
      return (
        rect.top <= 1 &&
        rect.left <= 1 &&
        rect.width >= window.innerWidth - 2 &&
        rect.height >= window.innerHeight - 2
      );
    };
    const finish = () => {
      if (settled || generation !== hoverGeneration) return;
      if (!windowEl.isConnected || !hoverLayer.classList.contains("is-active")) return;
      // Keep the previous photo until this one has actually covered it
      if (!coversViewport()) return;
      settled = true;
      settleHoverWindow(windowEl, generation);
    };

    windowEl.addEventListener("transitionend", (event) => {
      if (event.target !== windowEl || event.propertyName !== "top") return;
      finish();
    });

    let settleTries = 0;
    const pollSettle = () => {
      finish();
      settleTries += 1;
      if (settled || settleTries >= 24) return;
      if (generation !== hoverGeneration || !windowEl.isConnected) return;
      window.setTimeout(pollSettle, 80);
    };
    window.setTimeout(pollSettle, HOVER_REVEAL_MS);

    void windowEl.offsetWidth;

    // One continuous expand to the viewport, over the previous photo when switching cards
    requestAnimationFrame(() => {
      if (!hoverLayer.classList.contains("is-active")) return;
      if (generation !== hoverGeneration) return;
      withTransitions(windowEl, node, true);
      setWindowInset(windowEl, node, FULL_INSET);
    });
  }

  function clearHover() {
    if (!hoverLayer) return;
    cancelHoverClose();
    hoverGeneration += 1;
    resetParallax();
    hoverLayer.classList.remove("is-active");
    document.body.classList.remove("is-shop-hover");
    cards().forEach((card) => card.classList.remove("is-hovered"));
    clearNode(hoverLayer);
  }

  function fillOverlays(card) {
    clearNode(overlayA);
    clearNode(overlayB);

    const a = clonePreload(card, "[data-preload-overlay-a]", "shop-detail__overlay-image");
    const b = clonePreload(card, "[data-preload-overlay-b]", "shop-detail__overlay-image");

    if (overlayA) {
      overlayA.hidden = !a;
      overlayA.classList.remove("is-front");
      if (a) overlayA.appendChild(a);
    }
    if (overlayB) {
      overlayB.hidden = !b;
      overlayB.classList.remove("is-front");
      if (b) overlayB.appendChild(b);
    }
  }

  function stackLayers() {
    if (mode === "product") {
      return [...root.querySelectorAll("[data-stack-layer]")];
    }

    const layers = [];
    if (activeCard) {
      const primary = activeCard.querySelector('[data-stack-layer="primary"]');
      if (primary) layers.push(primary);
    }
    [overlayA, overlayB].forEach((el) => {
      if (el && !el.hidden) layers.push(el);
    });
    return layers;
  }

  function setFrontLayer(layer) {
    const layers = stackLayers();
    if (!layers.includes(layer)) return;

    layers.forEach((other) => {
      const front = other === layer;
      other.classList.toggle("is-front", front);
      other.style.zIndex = front ? "10" : "";
    });

    if (mode === "grid" && activeCard) {
      const primary = activeCard.querySelector('[data-stack-layer="primary"]');
      const primaryFront = layer === primary;
      activeCard.classList.toggle("is-front", primaryFront);
      // Fixed .shop-detail sits above the grid; drop it while primary is front
      if (detail) detail.classList.toggle("is-behind-primary", primaryFront);
    }
  }

  function clearFrontLayers() {
    root.querySelectorAll("[data-stack-layer]").forEach((el) => {
      el.classList.remove("is-front");
      el.style.zIndex = "";
    });
    cards().forEach((card) => {
      card.classList.remove("is-front");
    });
    if (detail) detail.classList.remove("is-behind-primary");
  }

  /** Keep the primary photo fully visible with a comfortable margin */
  function ensurePrimaryInView(card) {
    const media = card.querySelector("[data-shop-card-media]") || card;
    const rect = media.getBoundingClientRect();
    const margin = DETAIL_SCROLL_MARGIN;
    const topLimit = margin;
    const bottomLimit = window.innerHeight - margin;

    let delta = 0;
    if (rect.top < topLimit) {
      delta = rect.top - topLimit;
    } else if (rect.bottom > bottomLimit) {
      delta = rect.bottom - bottomLimit;
    }

    if (delta === 0) return;

    const target = (window.scrollY || window.pageYOffset) + delta;
    if (lenis) {
      lenis.scrollTo(target, { immediate: reduceMotion.matches, duration: 0.6 });
    } else {
      window.scrollBy({
        top: delta,
        behavior: reduceMotion.matches ? "auto" : "smooth",
      });
    }
  }

  function setBandSnapEnabled(enabled) {
    if (!bandSnap) return;
    if (enabled) bandSnap.start();
    else bandSnap.stop();
  }

  function destroySmoothScroll() {
    if (lenisRafId) {
      cancelAnimationFrame(lenisRafId);
      lenisRafId = null;
    }
    if (bandSnap) {
      bandSnap.destroy();
      bandSnap = null;
    }
    if (lenis) {
      lenis.destroy();
      lenis = null;
    }
  }

  /**
   * Desktop-only. Lerp (not a timed ease) so the page keeps gliding behind
   * the wheel — the weighted feel of GSAP ScrollSmoother. Snap only nudges
   * when you're already near a band, aligning that row to center.
   */
  function initSmoothScroll() {
    const Lenis = window.Lenis;
    const Snap = window.Snap;
    if (!Lenis || !Snap || mode !== "grid") return;

    function tick(time) {
      lenis?.raf(time);
      lenisRafId = requestAnimationFrame(tick);
    }

    function setup() {
      destroySmoothScroll();

      if (!mqDesktop.matches || reduceMotion.matches) return;

      lenis = new Lenis({
        lerp: 0.075,
        smoothWheel: true,
      });

      const items = bands();
      if (items.length) {
        bandSnap = new Snap(lenis, {
          type: "proximity",
          distanceThreshold: "35%",
          debounce: 120,
          duration: 0.85,
        });

        bandSnap.addElements(items, { align: "center" });

        if (detailOpen) bandSnap.stop();
      }

      lenisRafId = requestAnimationFrame(tick);
    }

    setup();
    mqDesktop.addEventListener("change", setup);
    reduceMotion.addEventListener("change", setup);
    window.addEventListener("resize", () => {
      if (bandSnap) bandSnap.resize();
    });
  }

  function writeGalleryCount(gallery, index) {
    const product = gallery.closest("[data-shop-panel-product]");
    const counter = product && product.querySelector("[data-panel-count]");
    if (counter && counter.dataset.countTemplate) {
      counter.textContent = counter.dataset.countTemplate.replace("__CURRENT__", String(index + 1));
    }
  }

  function mountPanelPreviews(product) {
    if (!product) return;
    product.querySelectorAll(".shop-panel__lqip").forEach((img) => {
      const url = img.dataset.lqip;
      if (!url || img.getAttribute("src")) return;
      img.src = url;
      if (typeof img.decode === "function") img.decode().catch(() => {});
    });
  }

  function panelSlides(gallery) {
    return [...gallery.querySelectorAll("[data-panel-slide]")];
  }

  function bumpSharpenToken(photo) {
    const next = Number(photo.dataset.sharpenToken || 0) + 1;
    photo.dataset.sharpenToken = String(next);
    return photo.dataset.sharpenToken;
  }

  function settlePhoto(photo) {
    photo.classList.add("is-sharp");
    photo.classList.remove("is-sharpening");
  }

  function previewPhoto(photo) {
    photo.classList.remove("is-sharp", "is-sharpening");
  }

  function whenSlideReady(img, fn) {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      fn();
    };
    const decode = () => {
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
        return;
      }
      finish();
    };
    if (img.complete) {
      if (img.naturalWidth > 0) decode();
      else finish();
      return;
    }
    img.loading = "eager";
    img.addEventListener("load", decode, { once: true });
    img.addEventListener("error", finish, { once: true });
  }

  /** Soft blur on every advance, then a one-second ease to sharp once the photo can paint. */
  function presentSlide(slide, { animate }) {
    const photo = slide.querySelector(".shop-panel__photo");
    const img = slide.querySelector(".shop-panel__image");
    if (!photo || !img) return;
    const token = bumpSharpenToken(photo);

    if (!animate || reduceMotion.matches) {
      settlePhoto(photo);
      return;
    }

    previewPhoto(photo);
    whenSlideReady(img, () => {
      if (photo.dataset.sharpenToken !== token) return;
      if (slide.classList.contains("is-inactive") && mqDesktop.matches) return;
      photo.classList.remove("is-sharp", "is-sharpening");
      void photo.offsetWidth;
      photo.classList.add("is-sharpening");
      photo.addEventListener(
        "animationend",
        (event) => {
          if (event.target !== photo) return;
          if (photo.dataset.sharpenToken !== token) return;
          settlePhoto(photo);
        },
        { once: true }
      );
    });
  }

  function releaseSlide(slide) {
    const photo = slide.querySelector(".shop-panel__photo");
    if (!photo) return;
    bumpSharpenToken(photo);
    previewPhoto(photo);
  }

  function setGalleryIndex(gallery, index, { scroll = false, animate = false } = {}) {
    const slides = panelSlides(gallery);
    const total = slides.length;
    if (!total) return;
    const next = (index + total) % total;
    if (scroll && !mqDesktop.matches) {
      gallery.scrollTo({
        left: next * gallery.clientWidth,
        behavior: reduceMotion.matches ? "auto" : "smooth",
      });
      return;
    }

    gallery.dataset.panelIndex = String(next);
    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle("is-inactive", slideIndex !== next);
    });
    writeGalleryCount(gallery, next);

    const sharpenNow = !animate || mqDesktop.matches;
    if (sharpenNow) {
      presentSlide(slides[next], { animate: animate && mqDesktop.matches });
      slides.forEach((slide, slideIndex) => {
        if (slideIndex !== next) releaseSlide(slide);
      });
    }

  }

  function resetPanelGallery(product) {
    const gallery = product.querySelector("[data-panel-gallery]");
    if (!gallery) return;
    gallery.dataset.panelMoved = "0";
    setGalleryIndex(gallery, 0, { animate: false });
    gallery.scrollLeft = 0;
  }

  function stepPanelGallery(gallery, delta) {
    const slides = gallery.querySelectorAll("[data-panel-slide]");
    if (slides.length < 2) return;
    const current = Number(gallery.dataset.panelIndex || 0);
    setGalleryIndex(gallery, current + delta, {
      scroll: !mqDesktop.matches,
      animate: true,
    });
  }

  function syncGalleryFromScroll(gallery) {
    if (mqDesktop.matches) return;
    const slides = panelSlides(gallery);
    const total = slides.length;
    if (total < 2) return;
    const width = gallery.clientWidth;
    if (width < 1) return;
    const next = Math.min(total - 1, Math.max(0, Math.round(gallery.scrollLeft / width)));
    if (gallery.dataset.panelIndex === String(next)) return;
    gallery.dataset.panelIndex = String(next);
    gallery.dataset.panelMoved = "1";
    slides.forEach((slide, index) => {
      slide.classList.toggle("is-inactive", index !== next);
    });
    writeGalleryCount(gallery, next);
  }

  function finishMobileGallery(gallery) {
    if (mqDesktop.matches) return;
    if (gallery.dataset.panelMoved !== "1") return;
    const slides = panelSlides(gallery);
    const total = slides.length;
    if (total < 2) return;
    const width = gallery.clientWidth;
    if (width < 1) return;
    const next = Math.min(total - 1, Math.max(0, Math.round(gallery.scrollLeft / width)));
    gallery.dataset.panelMoved = "0";
    gallery.dataset.panelIndex = String(next);
    slides.forEach((slide, index) => {
      const active = index === next;
      slide.classList.toggle("is-inactive", !active);
      if (active) presentSlide(slide, { animate: true });
      else releaseSlide(slide);
    });
    writeGalleryCount(gallery, next);
  }

  function bindPanelGallery() {
    if (!panel) return;

    panel.addEventListener("click", (event) => {
      if (!mqDesktop.matches) return;
      const gallery = event.target.closest("[data-panel-gallery]");
      if (!gallery || !gallery.classList.contains("shop-panel__frame--multi")) return;
      stepPanelGallery(gallery, 1);
    });

    panel.addEventListener("keydown", (event) => {
      const gallery = event.target.closest("[data-panel-gallery]");
      if (!gallery || !gallery.classList.contains("shop-panel__frame--multi")) return;
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      event.preventDefault();
      stepPanelGallery(gallery, event.key === "ArrowRight" ? 1 : -1);
    });

    panel.querySelectorAll("[data-panel-gallery]").forEach((gallery) => {
      let scrollTimer = 0;
      gallery.addEventListener(
        "scroll",
        () => {
          syncGalleryFromScroll(gallery);
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => finishMobileGallery(gallery), 140);
        },
        { passive: true }
      );
      gallery.addEventListener("scrollend", () => {
        window.clearTimeout(scrollTimer);
        finishMobileGallery(gallery);
      });
    });

    const visibleProduct = panel.querySelector("[data-shop-panel-product]:not([hidden])");
    if (visibleProduct) mountPanelPreviews(visibleProduct);

    panel.addEventListener(
      "wheel",
      (event) => {
        if (!mqDesktop.matches) return;
        const gallery = event.target.closest("[data-panel-gallery]");
        if (!gallery || !gallery.classList.contains("shop-panel__frame--multi")) return;
        event.preventDefault();
        const now = performance.now();
        if (now - Number(gallery.dataset.panelStepAt || 0) < 90) return;
        const delta =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (Math.abs(delta) < 4) return;
        gallery.dataset.panelStepAt = String(now);
        stepPanelGallery(gallery, delta > 0 ? 1 : -1);
      },
      { passive: false }
    );
  }

  function showPanelProduct(handle) {
    if (!panel) return;
    const products = panel.querySelectorAll("[data-shop-panel-product]");
    products.forEach((node) => {
      const show = node.dataset.shopPanelProduct === handle;
      node.hidden = !show;
      if (show) {
        mountPanelPreviews(node);
        resetPanelGallery(node);
      }
    });
    if (panelIntro) panelIntro.hidden = true;
    if (panelAbout) panelAbout.hidden = true;
    if (handle) lastProductHandle = handle;
    setHeaderView("shop");
  }

  function setHeaderView(view) {
    document.querySelectorAll("[data-header-home], [data-header-about], [data-header-shop]").forEach((link) => {
      const active =
        (view === "home" && link.hasAttribute("data-header-home")) ||
        (view === "about" && link.hasAttribute("data-header-about")) ||
        (view === "shop" && link.hasAttribute("data-header-shop"));
      if (active) link.setAttribute("aria-current", "true");
      else link.removeAttribute("aria-current");
    });
  }

  function showMobileAbout() {
    document.body.classList.add("is-mobile-about");
    showAbout();
  }

  function showMobileList() {
    document.body.classList.remove("is-mobile-about");
    document.body.classList.remove("is-shop-detail-open");
    detailOpen = false;
    activeCard = null;
    cards().forEach((card) => card.classList.remove("is-selected"));
    if (panel) {
      panel.querySelectorAll("[data-shop-panel-product]").forEach((node) => {
        node.hidden = true;
      });
    }
    if (panelIntro) panelIntro.hidden = true;
    if (panelAbout) panelAbout.hidden = true;
    setHeaderView("home");
    setBandSnapEnabled(true);
  }

  function showAbout() {
    if (!panel || !panelAbout) return;
    if (activeCard && activeCard.dataset.productHandle) {
      lastProductHandle = activeCard.dataset.productHandle;
    }
    panel.querySelectorAll("[data-shop-panel-product]").forEach((node) => {
      node.hidden = true;
    });
    if (panelIntro) panelIntro.hidden = true;
    panelAbout.hidden = false;
    setHeaderView("about");
  }

  function showLastProduct() {
    if (!lastProductHandle) {
      if (panel) {
        panel.querySelectorAll("[data-shop-panel-product]").forEach((node) => {
          node.hidden = true;
        });
      }
      if (panelIntro) panelIntro.hidden = true;
      if (panelAbout) panelAbout.hidden = true;
      setHeaderView("home");
      return;
    }

    if (panelAbout) panelAbout.hidden = true;

    const card = cards().find((item) => item.dataset.productHandle === lastProductHandle);
    if (card) {
      const alreadyThere = pathOf(window.location.pathname) === pathOf(card.dataset.productUrl);
      openDetail(card, { push: !alreadyThere && !detailOpen });
      return;
    }

    showPanelProduct(lastProductHandle);
  }

  function pathOf(url) {
    if (!url || url === "#") return "";
    try {
      const path = new URL(url, window.location.origin).pathname;
      return path.length > 1 ? path.replace(/\/$/, "") : path;
    } catch {
      return "";
    }
  }

  function cardForPath(pathname) {
    const current = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
    return cards().find((card) => pathOf(card.dataset.productUrl) === current) || null;
  }

  function homeUrl() {
    const brand = root.querySelector(".shop-panel__brand");
    return (brand && brand.getAttribute("href")) || "/";
  }

  function openDetail(card, { push = true } = {}) {
    if (!card || mode !== "grid") return;

    activeCard = card;
    detailOpen = true;
    setBandSnapEnabled(false);

    cards().forEach((c) => {
      c.classList.toggle("is-selected", c === card);
    });

    showPanelProduct(card.dataset.productHandle);
    document.body.classList.add("is-shop-detail-open");

    const url = card.dataset.productUrl;
    if (push && url && url !== "#") {
      history.pushState({ byDetail: true, handle: card.dataset.productHandle }, "", url);
      historyOwned = true;
    }
  }

  function closeDetail({ fromPop = false } = {}) {
    if (!detailOpen && !fromPop) return;

    document.body.classList.remove("is-shop-detail-open");
    detailOpen = false;

    cards().forEach((c) => c.classList.remove("is-selected"));
    if (panel) {
      panel.querySelectorAll("[data-shop-panel-product]").forEach((node) => {
        node.hidden = true;
      });
    }
    if (panelIntro) panelIntro.hidden = true;
    if (panelAbout) panelAbout.hidden = true;
    activeCard = null;
    setHeaderView("home");
    setBandSnapEnabled(true);

    if (!fromPop) {
      if (historyOwned) {
        historyOwned = false;
        history.back();
      } else if (cardForPath(window.location.pathname)) {
        history.pushState({ byDetail: false }, "", homeUrl());
      }
    }
  }

  /** Last-resort exit if in-place open is unavailable */
  function navigateWithFade(url) {
    document.body.classList.add("is-page-exit");
    window.setTimeout(() => {
      window.location.href = url;
    }, reduceMotion.matches ? 0 : 220);
  }

  function bindGrid() {
    // Capture phase so we always win over default link navigation
    root.addEventListener(
      "click",
      (event) => {
        const card = event.target.closest("[data-shop-card]");
        if (!card || !root.contains(card)) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }

        if (!mqDesktop.matches) {
          const url = card.dataset.productUrl;
          if (!url || url === "#") event.preventDefault();
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (detailOpen && activeCard === card) return;

        if (panel || detail) {
          openDetail(card, { push: true });
        } else {
          const link = card.querySelector("[data-shop-card-link]");
          const url = card.dataset.productUrl || (link && link.getAttribute("href"));
          if (url && url !== "#") navigateWithFade(url);
        }
      },
      true
    );

    const grid = root.querySelector("[data-shop-grid]");

    cards().forEach((card) => {
      card.addEventListener("pointerenter", () => {
        if (!mqDesktop.matches || reduceMotion.matches) return;
        requestHover(card);
      });

      card.addEventListener("pointermove", (event) => {
        if (!mqDesktop.matches || reduceMotion.matches) return;
        if (!card.classList.contains("is-hovered")) return;
        setParallaxFromPointer(event.clientX, event.clientY);
      });

      card.addEventListener("pointerleave", (event) => {
        if (!mqDesktop.matches) return;
        const next = event.relatedTarget;
        const nextCard =
          next instanceof Element ? next.closest("[data-shop-card]") : null;
        // A direct move onto another card keeps the photo up; that card's
        // pointerenter switches it. The grid gap is not a card, so close.
        if (nextCard && nextCard !== card && root.contains(nextCard)) return;
        requestHoverClear();
      });
    });

    if (grid) {
      grid.addEventListener("pointerleave", () => {
        if (!mqDesktop.matches) return;
        requestHoverClear();
      });
    }

    const closeBtn = root.querySelector("[data-shop-detail-close]");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => closeDetail());
    }

    window.addEventListener("popstate", () => {
      const card = cardForPath(window.location.pathname);
      historyOwned = false;
      if (card) openDetail(card, { push: false });
      else if (detailOpen) closeDetail({ fromPop: true });
    });

    warmCardMedia();
    bindPanelGallery();

    mqDesktop.addEventListener("change", () => {
      if (mqDesktop.matches) return;
      if (detailOpen) {
        const url = activeCard && activeCard.dataset.productUrl;
        if (url && url !== "#" && document.body.classList.contains("template-index")) {
          window.location.assign(url);
        }
        return;
      }
      if (panelAbout && !panelAbout.hidden) {
        document.body.classList.add("is-mobile-about");
      }
    });

    document.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const aboutLink = event.target.closest("[data-header-about]");
      const shopLink = event.target.closest("[data-header-shop]");
      const homeLink = event.target.closest("[data-header-home]");

      if (!mqDesktop.matches) {
        if (aboutLink) {
          event.preventDefault();
          showMobileAbout();
          return;
        }
        if ((homeLink || shopLink) && document.body.classList.contains("template-index")) {
          event.preventDefault();
          showMobileList();
        }
        return;
      }

      if (aboutLink) {
        event.preventDefault();
        showAbout();
        return;
      }

      if (shopLink) {
        event.preventDefault();
        showLastProduct();
      }
    });
  }

  function bindStackLayers() {
    root.querySelectorAll("[data-stack-layer]").forEach((el) => {
      el.addEventListener("pointerenter", () => {
        if (mode === "grid" && !detailOpen) return;
        setFrontLayer(el);
      });
    });
  }

  document.addEventListener("click", async (event) => {
    const atc = event.target.closest("[data-add-to-cart]");
    if (!atc || atc.disabled) return;
    event.preventDefault();
    const variantId = atc.dataset.variantId;
    if (!variantId || !window.ByCart) return;

    try {
      await window.ByCart.addToCart(variantId, 1);
      const cart = await window.ByCart.fetchCart();
      await window.ByCart.renderLines(cart);
      window.ByCart.openDrawer();
    } catch (err) {
      console.error(err);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && detailOpen && mode === "grid") {
      closeDetail();
    }
  });

  if (mode === "grid") {
    if (overlayA) overlayA.hidden = true;
    if (overlayB) overlayB.hidden = true;
    bindGrid();
    bindStackLayers();
    initSmoothScroll();
    const initial =
      cards().find((card) => card.classList.contains("is-selected")) ||
      cardForPath(window.location.pathname);
    if (initial) openDetail(initial, { push: false });
  } else {
    bindStackLayers();
    document.body.classList.add("is-shop-detail-open");
  }
})();
